import { altitudeToZoom, lv95ToWgs84, maximumPitch, zoomToAltitude } from './camera.ts';
import {
  containsCoordinate,
  createCameraState,
  createLngLat,
  type CameraState,
  type GeoBounds,
} from './coordinates.ts';

interface CameraHashContext {
  readonly bounds: GeoBounds;
  readonly viewportHeight: number;
}

function numericParts(value: string): readonly number[] | undefined {
  const parts = value.split(',');
  if (parts.length < 3 || parts.length > 5) return undefined;
  const values: number[] = [];
  for (const part of parts) {
    if (part === '' || part.trim() !== part) return undefined;
    const parsed = Number(part);
    if (!Number.isFinite(parsed)) return undefined;
    values.push(parsed);
  }
  return values;
}

export function parseCameraHash(
  hash: string,
  { bounds, viewportHeight }: CameraHashContext,
): CameraState | undefined {
  try {
    const parameters = new URLSearchParams(hash.replace(/^#/u, ''));
    const cameraValues = parameters.getAll('c');
    const spatialReferences = parameters.getAll('sr');
    if (cameraValues.length !== 1 || spatialReferences.length > 1) return undefined;
    const parts = numericParts(cameraValues[0] ?? '');
    if (parts === undefined) return undefined;
    const [first, second, altitude, heading = 0, tilt = 0] = parts;
    if (first === undefined || second === undefined || altitude === undefined || altitude <= 0) {
      return undefined;
    }
    const spatialReference = spatialReferences[0] ?? '4326';
    const center =
      spatialReference === '4326'
        ? createLngLat(first, second)
        : spatialReference === '2056'
          ? lv95ToWgs84(first, second)
          : undefined;
    if (center === undefined || !containsCoordinate(bounds, center)) return undefined;
    const pitch = Math.min(Math.max(tilt, 0), maximumPitch);
    return createCameraState({
      center,
      zoom: altitudeToZoom({ altitude, latitude: center[1], pitch, viewportHeight }),
      bearing: heading,
      pitch,
    });
  } catch {
    return undefined;
  }
}

function roundToTenth(value: number): number {
  const rounded = Math.round(value * 10) / 10;
  return Object.is(rounded, -0) ? 0 : rounded;
}

export function formatCameraHash(camera: CameraState, viewportHeight: number): string {
  const pitch = roundToTenth(camera.pitch);
  const heading = roundToTenth(((camera.bearing % 360) + 360) % 360);
  const altitude = Math.round(
    zoomToAltitude({
      latitude: camera.center[1],
      pitch,
      viewportHeight,
      zoom: camera.zoom,
    }),
  );
  const parts: (number | string)[] = [
    camera.center[0].toFixed(6),
    camera.center[1].toFixed(6),
    altitude,
  ];
  if (pitch > 0) parts.push(heading, pitch);
  else if (heading !== 0) parts.push(heading);
  return `#c=${parts.join(',')}`;
}
